import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import posthog from 'posthog-js';
import { authApi, getStoredCompanyId, setStoredCompanyId, clearStoredCompanyId,
         JUST_SWITCHED_KEY } from '../services/api';

const AuthContext = createContext(null);

/* 2026-08-02 (admin F4): PostHog identitás- és tenant-varrat lezárása.
 *
 * Eddig SEHOL nem volt `identify()` és `reset()` — minden felhasználó anonim
 * volt, és a `group('company', ...)` az adószámot kapta helyettesítőként
 * (a CLAUDE.md ezt cserélendő pontként dokumentálta). Két következménye volt:
 *   - nem lehetett megmondani, hány KÜLÖNBÖZŐ ember használja a rendszert;
 *   - közös gépen a kijelentkezés után is ugyanaz a distinct_id élt tovább,
 *     tehát két ember eseményei összekeveredtek.
 *
 * ⚠ A `distinct_id` a Supabase user UUID — az e-mail cím SZÁNDÉKOSAN nem megy
 * ki tulajdonságként. A megkülönböztetéshez a UUID elég; egy e-mail cím már
 * személyes adat lenne egy külső szolgáltatónál.
 *
 * 2026-09-04 (M1): a csoport az AKTÍV cég, nem a memberships[0] — több cégnél a
 * kettő eltérhet, és egy rossz csoportba sorolt esemény később kideríthetetlen.
 */
function syncAnalyticsIdentity(user, memberships, activeCompanyId) {
  try {
    if (!user?.id) return;
    posthog.identify(user.id);
    const active = (memberships || []).find(m => m.company_id === activeCompanyId)
                || memberships?.[0];
    if (active?.company_id) {
      // Valódi company_id az adószám-helyettesítő HELYETT.
      posthog.group('company', String(active.company_id),
                    active.company_name ? { name: active.company_name } : undefined);
    }
  } catch { /* az analitika sose törje el a bejelentkezést */ }
}

function resetAnalyticsIdentity() {
  try { posthog.reset(); } catch { /* no-op */ }
}

/* Az aktív cég kliens-oldali feloldása (M1, 2026-09-04).
 *
 * A tárolt `pf_active_company_id` csak akkor érvényes, ha a user MOST is tagja
 * annak a cégnek — különben töröljük (pl. közös gépen másik user lépett be, vagy
 * a tagságot időközben az admin visszavonta). A backend `/auth/me` válaszában
 * jövő `active_company_id` a validált igazság (a fejlécet a szerver ellenőrizte);
 * login-válaszban ez a mező nincs (a login nem megy át a resolveren), ott a
 * tárolt érték a tagságok ellen ellenőrizve, végül a memberships[0] a fallback
 * — pontosan az, amit a backend fejléc nélkül is feloldana. */
function resolveActiveCompany(memberships, serverActiveId) {
  const ids = (memberships || []).map(m => m.company_id);
  const stored = getStoredCompanyId();
  if (stored != null && !ids.includes(stored)) clearStoredCompanyId();
  if (serverActiveId != null && ids.includes(serverActiveId)) return serverActiveId;
  if (stored != null && ids.includes(stored)) return stored;
  return ids[0] ?? null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyAuthData = useCallback((data, serverActiveId) => {
    const ms = data.memberships || [];
    const active = resolveActiveCompany(ms, serverActiveId);
    setUser(data.user);
    setMemberships(ms);
    setActiveCompanyId(active);
    syncAnalyticsIdentity(data.user, ms, active);
  }, []);

  const refetchMe = useCallback(async () => {
    try {
      const data = await authApi.me();
      applyAuthData(data, data.active_company_id ?? null);
    } catch {
      setUser(null);
      setMemberships([]);
      setActiveCompanyId(null);
    }
  }, [applyAuthData]);

  useEffect(() => {
    refetchMe().finally(() => setLoading(false));
  }, [refetchMe]);

  const login = useCallback(async (email, password, remember = false, turnstileToken = null) => {
    const data = await authApi.login(email, password, remember, turnstileToken);
    applyAuthData(data, null);
    return data;
  }, [applyAuthData]);

  // A passkey-ceremóniát (navigator.credentials.get()) a Login oldal futtatja
  // le - ez a hívó csak a kész {user, memberships} eredményt veszi át, ugyanúgy,
  // mint a jelszavas login után.
  const applyLoginResult = useCallback((data) => {
    applyAuthData(data, null);
  }, [applyAuthData]);

  /* Cégváltás (M1). SZÁNDÉKOSAN teljes oldal-újratöltés a `/`-ra, NEM finomabb
   * cache-invalidáció: a React Query kulcsok (`['settings']`, `['company']`,
   * `['revolut-env-status']`, ...) ma NINCSENEK cég-scope-olva, tehát egy
   * invalidálás mellett egy pillanatra a RÉGI cég adata látszana az ÚJ cégnek
   * látszó fejléc alatt. És `/`-ra, nem `reload()`-dal az aktuális útvonalon:
   * egy `/documents/:docId` deep-link a régi cég bizonylatára mutatna, ami az
   * új cég kontextusában 403/404. A visszajelzést (toast) az új oldal adja a
   * `pf_just_switched` sessionStorage-flagből (Layout.js). */
  const switchCompany = useCallback((companyId, companyName) => {
    setStoredCompanyId(companyId);
    try {
      sessionStorage.setItem(JUST_SWITCHED_KEY,
        JSON.stringify({ id: companyId, name: companyName || '' }));
    } catch { /* no-op */ }
    window.location.href = '/';
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } finally {
      setUser(null);
      setMemberships([]);
      setActiveCompanyId(null);
      // A tárolt aktív céget SZÁNDÉKOSAN nem töröljük: ugyanaz a user
      // visszatérve ott folytatja. Másik user belépésekor a tagság-ellenőrzés
      // (`resolveActiveCompany`) úgyis eldobja, ha nem az övé.
      // Közös gépen kötelező: enélkül a következő belépő a mi
      // distinct_id-nkkal küldene eseményeket.
      resetAnalyticsIdentity();
    }
  }, []);

  // M11 (2026-09-05): az AKTÍV tagság szerepköre a szerepkör-alapú feltételes
  // rendereléshez (író gombok `disabled` + tooltip CO_WORKER-nek — NEM elrejtve).
  // A backend enum értéke lowercase (`admin` | `co_worker`), tűrő összehasonlítás.
  const activeRole = (memberships || []).find(m => m.company_id === activeCompanyId)?.role || null;
  const isAdmin = String(activeRole || '').toLowerCase() === 'admin';

  return (
    <AuthContext.Provider value={{ user, memberships, activeCompanyId, activeRole, isAdmin, loading,
                                   login, logout, applyLoginResult, switchCompany }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth csak AuthProvideren belül használható');
  return ctx;
}
